import React, { useState, useRef } from 'react';
import { Modal, ModalBody, Button, Row, Col, Spinner } from 'reactstrap';
import { FiUploadCloud, FiFile, FiCheckCircle, FiXCircle, FiX } from 'react-icons/fi';
import axios from 'axios';
import { toast } from 'react-toastify';
import { APP_TOKEN } from 'constants';

const CRM_API_ROOT = `${process.env.REACT_APP_REST_API_ENDPOINT || ''}crm`;

const getStoredAuth = () => {
  if (typeof window === 'undefined') return null;

  const storedAuth = window.localStorage.getItem(APP_TOKEN);
  if (!storedAuth) return null;

  try {
    return JSON.parse(storedAuth);
  } catch (error) {
    return null;
  }
};

const getStoredToken = () => {
  const auth = getStoredAuth();
  return (
    auth?.data?.token ||
    auth?.token ||
    auth?.accessToken ||
    auth?.jwt ||
    auth?.userLogin?.token ||
    null
  );
};

const UploadCsvModal = ({ isOpen, toggle, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
      } else {
        toast.error('Please upload a valid CSV file.');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    const token = getStoredToken();
    if (!token) {
      toast.error('Authentication token not found.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${CRM_API_ROOT}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const { data } = response;
      setImportSummaryData(data);

      if (data?.successRecords > 0) {
        toast.success(data?.message || 'CSV file imported successfully.');
      }

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      if (error?.response?.data && typeof error?.response?.data?.duplicateRecords === 'number') {
        setImportSummaryData(error.response.data);
        return;
      }
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.warning;
      toast.error(apiMessage || error?.message || 'Failed to import CSV file.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportSummaryData(null);
    toggle();
  };

  const resetUpload = () => {
    setFile(null);
    setImportSummaryData(null);
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalBody className="p-0 rounded overflow-hidden shadow-lg border-0 bg-white">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center p-4 border-bottom bg-white">
          <h5 className="mb-0 text-dark fw-bold">
            {importSummaryData ? 'Import Summary' : 'Upload CSV Leads'}
          </h5>
          <button
            type="button"
            className="btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center border"
            style={{ width: '36px', height: '36px' }}
            onClick={handleClose}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-4">
          {importSummaryData ? (
            <>
              {/* Summary View */}
              <div className="mb-4">
                <div className="alert border rounded-4 d-flex align-items-center gap-3 p-4 bg-white text-dark">
                  {importSummaryData.successRecords > 0 ? <FiCheckCircle size={28} /> : <FiXCircle size={28} />}
                  <div>
                    <h6 className="mb-1 fw-bold">{importSummaryData.message}</h6>
                    <p className="mb-0 small text-muted">Review the import details below.</p>
                  </div>
                </div>

                <Row className="g-3 mt-2">
                  <Col md={3} xs={6}>
                    <div className="p-3 border rounded-4 text-center bg-white h-100">
                      <div className="fs-2 fw-bold text-dark mb-1">{importSummaryData.totalRecords || 0}</div>
                      <div className="text-muted small fw-medium text-uppercase tracking-wide">Total</div>
                    </div>
                  </Col>
                  <Col md={3} xs={6}>
                    <div className="p-3 border rounded-4 text-center bg-white h-100">
                      <div className="fs-2 fw-bold text-dark mb-1">{importSummaryData.successRecords || 0}</div>
                      <div className="text-muted small fw-medium text-uppercase tracking-wide">Success</div>
                    </div>
                  </Col>
                  <Col md={3} xs={6}>
                    <div className="p-3 border rounded-4 text-center bg-white h-100">
                      <div className="fs-2 fw-bold text-dark mb-1">{importSummaryData.failedRecords || 0}</div>
                      <div className="text-muted small fw-medium text-uppercase tracking-wide">Failed</div>
                    </div>
                  </Col>
                  <Col md={3} xs={6}>
                    <div className="p-3 border rounded-4 text-center bg-white h-100">
                      <div className="fs-2 fw-bold text-dark mb-1">{importSummaryData.duplicateRecords || 0}</div>
                      <div className="text-muted small fw-medium text-uppercase tracking-wide">Duplicates</div>
                    </div>
                  </Col>
                </Row>

                {importSummaryData.errors && importSummaryData.errors.length > 0 && (
                  <div className="mt-4">
                    <h6 className="fw-bold text-dark mb-3">Error Details</h6>
                    <div className="p-3 border rounded-4 bg-white" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <ul className="mb-0 text-dark small list-unstyled">
                        {importSummaryData.errors.map((err, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <li key={idx} className="mb-2 d-flex align-items-start gap-2">
                            <FiXCircle className="mt-1 flex-shrink-0" size={14} />
                            <span>{err}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between mt-4">
                <Button color="light" className="px-4 rounded-pill fw-medium text-dark border" onClick={resetUpload}>
                  Upload Another File
                </Button>
                <Button color="dark" className="px-5 rounded-pill fw-medium" onClick={handleClose}>
                  Done
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Upload View */}
              {file ? (
                <div className="border rounded-4 p-4 mb-4 bg-white d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3 overflow-hidden">
                    <div className="bg-white p-3 rounded-circle border text-dark">
                      <FiFile size={24} />
                    </div>
                    <div className="text-truncate">
                      <h6 className="mb-1 text-dark fw-semibold text-truncate">{file.name}</h6>
                      <small className="text-muted">{(file.size / 1024).toFixed(2)} KB</small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-dark btn-sm rounded-pill px-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    disabled={isUploading}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  className={`border-2 border-dashed rounded-4 p-5 text-center transition-all ${
                    isDragging ? 'border-dark bg-light' : 'border-secondary border-opacity-25 bg-white'
                  }`}
                  style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  <input
                    type="file"
                    accept=".csv"
                    className="d-none"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  <div className="mb-3 text-dark opacity-75">
                    <FiUploadCloud size={48} />
                  </div>
                  <h5 className="text-dark fw-semibold mb-2">Click or drag CSV file to upload</h5>
                  <p className="text-muted small mb-0">Only .csv files are supported</p>
                </div>
              )}

              {/* Actions */}
              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button color="light" className="px-4 rounded-pill fw-medium text-dark border" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  color="dark"
                  className="px-5 rounded-pill fw-medium d-flex align-items-center gap-2"
                  onClick={handleUpload}
                  disabled={!file || isUploading}
                >
                  {isUploading ? (
                    <>
                      <Spinner size="sm" /> Uploading...
                    </>
                  ) : (
                    'Upload File'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default UploadCsvModal;